import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Move } from "lucide-react";

const VB_W = 1000;
const VB_H = 820;
const SNAP = 5;
const MIN_W = 40;
const MIN_H = 40;

const snap = (n) => Math.round(n / SNAP) * SNAP;

const STATUS_FILL = {
  available: "#15803D",
  pending: "#B45309",
  leased: "#64748B",
};

export default function SitePlanEditor() {
  const svgRef = useRef(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState({}); // lot_number -> true
  const [drag, setDrag] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/lots").then((r) => setLots(r.data)).catch(() => toast.error("Could not load lots")).finally(() => setLoading(false));
  }, []);

  // Convert mouse event to SVG coordinates
  const toSvg = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    return pt.matrixTransform(ctm.inverse());
  };

  const startMove = (e, lot) => {
    e.stopPropagation();
    e.preventDefault();
    const p = toSvg(e);
    setSelected(lot.lot_number);
    setDrag({
      mode: "move", lot_number: lot.lot_number,
      startX: p.x, startY: p.y,
      origX: lot.x, origY: lot.y, origW: lot.w, origH: lot.h,
    });
  };

  const startResize = (e, lot) => {
    e.stopPropagation();
    e.preventDefault();
    const p = toSvg(e);
    setSelected(lot.lot_number);
    setDrag({
      mode: "resize", lot_number: lot.lot_number,
      startX: p.x, startY: p.y,
      origX: lot.x, origY: lot.y, origW: lot.w, origH: lot.h,
    });
  };

  const onMouseMove = (e) => {
    if (!drag) return;
    const p = toSvg(e);
    const dx = p.x - drag.startX;
    const dy = p.y - drag.startY;
    setLots((prev) => prev.map((l) => {
      if (l.lot_number !== drag.lot_number) return l;
      if (drag.mode === "move") {
        let nx = snap(Math.max(0, Math.min(VB_W - drag.origW, drag.origX + dx)));
        let ny = snap(Math.max(0, Math.min(VB_H - drag.origH, drag.origY + dy)));
        return { ...l, x: nx, y: ny };
      } else {
        let nw = snap(Math.max(MIN_W, drag.origW + dx));
        let nh = snap(Math.max(MIN_H, drag.origH + dy));
        nw = Math.min(nw, VB_W - drag.origX);
        nh = Math.min(nh, VB_H - drag.origY);
        return { ...l, w: nw, h: nh };
      }
    }));
  };

  const endDrag = () => {
    if (!drag) return;
    setDirty((d) => ({ ...d, [drag.lot_number]: true }));
    setDrag(null);
  };

  const selectedLot = lots.find((l) => l.lot_number === selected);

  const updateField = (key, value) => {
    if (!selectedLot) return;
    setLots((prev) => prev.map((l) => l.lot_number === selected ? { ...l, [key]: value } : l));
    setDirty((d) => ({ ...d, [selected]: true }));
  };

  const saveAll = async () => {
    const dirtyNums = Object.keys(dirty).map(Number);
    if (!dirtyNums.length) {
      toast.info("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      for (const num of dirtyNums) {
        const l = lots.find((x) => x.lot_number === num);
        if (!l) continue;
        await api.patch(`/admin/lots/${num}`, {
          x: l.x, y: l.y, w: l.w, h: l.h,
          sq_ft: parseInt(l.sq_ft), acres: parseFloat(l.acres),
          monthly_rent: parseFloat(l.monthly_rent),
          status: l.status,
        });
      }
      toast.success(`Saved ${dirtyNums.length} lot${dirtyNums.length > 1 ? "s" : ""}`);
      setDirty({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetLayout = async () => {
    if (!window.confirm("Reset ALL lots to the original PDF layout? This discards any edits.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/lots/reset-layout");
      setLots(data.lots);
      setDirty({});
      toast.success("Layout reset to original");
    } catch {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div data-testid="site-plan-editor" className="grid lg:grid-cols-12 gap-6">
      {/* SVG Editor */}
      <div className="lg:col-span-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Drag lots to move · Drag yellow corner to resize</div>
            <h2 className="mt-1 font-display font-black text-xl uppercase tracking-tight text-[#0B0C0E]">Site Plan Layout</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetLayout} disabled={saving}
              className="border-2 border-[#0B0C0E] hover:bg-[#FACC15] disabled:opacity-50 text-[#0B0C0E] font-bold px-4 py-2 text-xs uppercase inline-flex items-center gap-2"
              data-testid="reset-layout-btn"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
            <button
              onClick={saveAll} disabled={saving || !dirtyCount}
              className="bg-[#0B0C0E] hover:bg-black disabled:opacity-40 text-white font-bold px-5 py-2 text-xs uppercase tracking-wide inline-flex items-center gap-2 border-l-4 border-[#FACC15]"
              data-testid="save-layout-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
            </button>
          </div>
        </div>

        <div className="border-2 border-[#0B0C0E] bg-white">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            className="w-full h-auto block select-none"
            style={{ aspectRatio: `${VB_W} / ${VB_H}`, touchAction: "none" }}
            onMouseMove={onMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onClick={() => setSelected(null)}
          >
            {/* Background grid */}
            <defs>
              <pattern id="editor-grid" patternUnits="userSpaceOnUse" width={20} height={20}>
                <path d={`M 20 0 L 0 0 0 20`} fill="none" stroke="#E5E3DA" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect x="0" y="0" width={VB_W} height={VB_H} fill="#FCFBF6" />
            <rect x="0" y="0" width={VB_W} height={VB_H} fill="url(#editor-grid)" />
            <rect x="8" y="8" width={VB_W - 16} height={VB_H - 16} fill="none" stroke="#0B0C0E" strokeWidth="2" />

            {/* Lots */}
            {lots.map((lot) => {
              const isSelected = selected === lot.lot_number;
              const fill = STATUS_FILL[lot.status] || "#15803D";
              return (
                <g key={lot.lot_number} data-testid={`editor-lot-${lot.lot_number}`}>
                  <rect
                    x={lot.x} y={lot.y} width={lot.w} height={lot.h}
                    fill={fill} fillOpacity={isSelected ? 0.45 : 0.22}
                    stroke={isSelected ? "#FACC15" : "#0B0C0E"}
                    strokeWidth={isSelected ? 3 : 1.2}
                    style={{ cursor: "move" }}
                    onMouseDown={(e) => startMove(e, lot)}
                    onClick={(e) => { e.stopPropagation(); setSelected(lot.lot_number); }}
                  />
                  <text
                    x={lot.x + lot.w / 2} y={lot.y + lot.h / 2 + 6}
                    textAnchor="middle" fontFamily="Cabinet Grotesk"
                    fontWeight="900" fontSize="22" fill="#0B0C0E"
                    pointerEvents="none"
                  >{`LOT ${lot.lot_number}`}</text>

                  {/* Resize handle (bottom-right) — only on selected */}
                  {isSelected && (
                    <rect
                      x={lot.x + lot.w - 9} y={lot.y + lot.h - 9}
                      width={18} height={18}
                      fill="#FACC15" stroke="#0B0C0E" strokeWidth={2}
                      style={{ cursor: "nwse-resize" }}
                      onMouseDown={(e) => startResize(e, lot)}
                      data-testid={`resize-handle-${lot.lot_number}`}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        <div className="mt-3 text-xs text-[#56606E] flex items-center gap-2">
          <Move className="w-3 h-3" /> Click any lot to select. Drag the body to move. Drag the yellow corner to resize. Positions snap to a 5-unit grid.
        </div>
      </div>

      {/* Side panel */}
      <aside className="lg:col-span-4">
        <div className="border-2 border-[#0B0C0E] bg-white p-6 sticky top-24">
          <div className="overline">Selected Lot</div>
          {!selectedLot ? (
            <div className="mt-3 text-sm text-[#56606E]">Click a lot in the plan to edit its position, size, and details.</div>
          ) : (
            <>
              <div className="mt-2 flex items-baseline justify-between">
                <div className="font-display font-black text-3xl text-[#0B0C0E]">Lot {selectedLot.lot_number}</div>
                {dirty[selectedLot.lot_number] && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">UNSAVED</span>
                )}
              </div>

              <div className="overline mt-6 mb-2">Position (SVG units)</div>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="X" value={selectedLot.x} onChange={(v) => updateField("x", v)} testId={`field-x-${selectedLot.lot_number}`} />
                <NumField label="Y" value={selectedLot.y} onChange={(v) => updateField("y", v)} testId={`field-y-${selectedLot.lot_number}`} />
                <NumField label="Width" value={selectedLot.w} onChange={(v) => updateField("w", v)} testId={`field-w-${selectedLot.lot_number}`} />
                <NumField label="Height" value={selectedLot.h} onChange={(v) => updateField("h", v)} testId={`field-h-${selectedLot.lot_number}`} />
              </div>

              <div className="overline mt-6 mb-2">Real-world Size & Rent</div>
              <NumField label="Square Feet" value={selectedLot.sq_ft} onChange={(v) => updateField("sq_ft", v)} step={1} testId={`field-sqft-${selectedLot.lot_number}`} />
              <NumField label="Acres" value={selectedLot.acres} onChange={(v) => updateField("acres", v)} step={0.01} testId={`field-acres-${selectedLot.lot_number}`} />
              <NumField label="Monthly Rent ($)" value={selectedLot.monthly_rent} onChange={(v) => updateField("monthly_rent", v)} step={1} testId={`field-rent-${selectedLot.lot_number}`} />

              <div className="overline mt-6 mb-2">Status</div>
              <select
                value={selectedLot.status}
                onChange={(e) => updateField("status", e.target.value)}
                className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                data-testid={`field-status-${selectedLot.lot_number}`}
              >
                <option value="available">available</option>
                <option value="pending">pending</option>
                <option value="leased">leased</option>
              </select>

              <button
                onClick={saveAll} disabled={saving || !dirty[selectedLot.lot_number]}
                className="mt-6 w-full bg-[#0B0C0E] hover:bg-black disabled:opacity-40 text-white font-bold py-3 text-xs uppercase tracking-wide inline-flex items-center justify-center gap-2 border-l-4 border-[#FACC15]"
                data-testid={`save-lot-${selectedLot.lot_number}`}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Lot
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

const NumField = ({ label, value, onChange, step = 5, testId }) => (
  <div>
    <label className="overline mb-1 block">{label}</label>
    <input
      type="number" step={step} value={value ?? 0}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? 0 : (step < 1 ? parseFloat(v) : parseFloat(v)));
      }}
      className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
      data-testid={testId}
    />
  </div>
);
